/**
 * Single source of truth for the public business-card share URL.
 *
 * iter175cj — Share URL is ALWAYS the brand domain. The path goes
 * through the backend's OG endpoint (`/api/og/in/<decoy>`) so that:
 *   - iMessage / Slack / Twitter / Facebook / WhatsApp etc. that fetch
 *     the URL with a crawler User-Agent get a server-rendered HTML
 *     response with per-user OpenGraph + Twitter Card meta tags —
 *     name, title, company, photo — so the rich preview looks right.
 *   - Real browsers visiting the URL get the same response BUT with a
 *     JS + meta-refresh redirect to `/in/<decoy>` on the same brand
 *     domain, so the user-visible URL after the redirect is clean
 *     (`https://www.orbuplift.com/in/<decoy>`) and the SPA renders
 *     the business card identically to before.
 *
 * The hostname iMessage etc. display in the preview comes from the
 * URL we shared — i.e. orbuplift.com — NEVER the Emergent preview
 * host. The `/api/og/in/` path segment is small in the preview UI
 * (most clients show the hostname only).
 *
 * Falls back to `<window.location.origin>/api/og/in/<decoy>` when the
 * env var is missing (dev environments without the brand domain
 * configured).
 */
export function getCardShareUrl(decoyId) {
  if (!decoyId) return '';
  const base = (process.env.REACT_APP_CARD_SHARE_BASE_URL || '').trim();
  // The configured env var is `https://www.orbuplift.com/in` (the v3
  // path). We strip that trailing `/in` so we can re-mount the path
  // at `/api/og/in/<decoy>` — the OG endpoint on the SAME brand host.
  const stripTrailingIn = (s) => s.replace(/\/$/, '').replace(/\/in$/, '');
  if (base) {
    try {
      const origin = stripTrailingIn(new URL(base).origin + new URL(base).pathname);
      return `${origin}/api/og/in/${decoyId}`;
    } catch (_e) {/* malformed env var — fall through */}
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api/og/in/${decoyId}`;
  }
  return `/api/og/in/${decoyId}`;
}
